import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Visibility as VisibilityIcon, 
  Search as SearchIcon,
  Clear as ClearIcon,
  Info as InfoIcon 
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import format from 'date-fns/format';
import { subDays, startOfMonth, endOfMonth, startOfYear, endOfYear, parseISO } from 'date-fns';
import api from '../../services/api';
import { useAuth } from '../../contexts/auth';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination';
import UserAvatar from '../../components/UserAvatar';
import { PROFESSIONAL_PROFILES } from '../../constants/profiles';

const DiaryList = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user, signed, loading: authLoading, validateToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [diaries, setDiaries] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [dateRange, setDateRange] = useState('today');
  const [customDate, setCustomDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedDiaries, setSelectedDiaries] = useState([]);
  const [confirmArchiveDialog, setConfirmArchiveDialog] = useState(false);
  const initialLoadAttempted = useRef(false);

  const dateRangeOptions = [
    { value: 'today', label: 'Hoje' },
    { value: 'yesterday', label: 'Ontem' },
    { value: 'last7', label: 'Últimos 7 dias' },
    { value: 'last15', label: 'Últimos 15 dias' },
    { value: 'thisMonth', label: 'Este mês' },
    { value: 'thisYear', label: 'Este ano' },
    { value: 'all', label: 'Todo o período' },
    { value: 'custom', label: 'Data específica' }
  ];

  // Definir getDateRange com useCallback antes de usá-la em fetchDiaries
  const getDateRange = useCallback(() => {
    const today = new Date();
    
    // Função auxiliar para formatar datas de forma segura
    const formatDate = (date) => {
      try {
        return format(date, 'yyyy-MM-dd');
      } catch (error) {
        console.error('Erro ao formatar data:', error, date);
        return format(new Date(), 'yyyy-MM-dd');
      }
    };
    
    switch (dateRange) {
      case 'today':
        return {
          startDate: formatDate(today),
          endDate: formatDate(today)
        };
      case 'yesterday': {
        const yesterday = subDays(today, 1);
        return {
          startDate: formatDate(yesterday),
          endDate: formatDate(yesterday)
        };
      }
      case 'last7': {
        const sevenDaysAgo = subDays(today, 6); // 6 dias atrás + hoje = 7 dias
        return {
          startDate: formatDate(sevenDaysAgo),
          endDate: formatDate(today)
        };
      }
      case 'last15': {
        const fifteenDaysAgo = subDays(today, 14); // 14 dias atrás + hoje = 15 dias
        return {
          startDate: formatDate(fifteenDaysAgo),
          endDate: formatDate(today)
        };
      }
      case 'thisMonth':
        return {
          startDate: formatDate(startOfMonth(today)),
          endDate: formatDate(endOfMonth(today))
        };
      case 'thisYear':
        return {
          startDate: formatDate(startOfYear(today)),
          endDate: formatDate(endOfYear(today))
        };
      case 'custom':
        // Garantir que a data personalizada é válida
        try {
          const parsedDate = customDate ? parseISO(customDate) : today;
          return {
            startDate: formatDate(parsedDate),
            endDate: formatDate(parsedDate)
          };
        } catch (error) {
          console.error('Erro ao processar data personalizada:', error);
          return {
            startDate: formatDate(today),
            endDate: formatDate(today)
          };
        }
      default:
        return {
          startDate: null,
          endDate: null
        };
    }
  }, [dateRange, customDate]);

  // Definir a função fetchDiaries com useCallback antes de usá-la no useEffect
  const fetchDiaries = useCallback(async (searchTerm = searchInput) => {
    try {
      setLoading(true);
      const dateParams = getDateRange();
      
      console.log('Buscando diários com os parâmetros:', {
        page,
        searchTerm,
        dateRange: dateParams
      });
      
      const response = await api.get('/api/diary', {
        params: {
          page,
          search: searchTerm,
          startDate: dateParams.startDate,
          endDate: dateParams.endDate
        },
      });
      
      console.log('Resposta da API de diários:', response.data);
      setDiaries(response.data.diaries);
      setTotalPages(response.data.pages);
      return true;
    } catch (error) {
      console.error('Erro ao carregar diários:', error);
      // Verificar se o erro é de autenticação
      if (error.response && error.response.status === 401) {
        enqueueSnackbar('Sessão expirada. Redirecionando para login...', { variant: 'error' });
        throw error; // Propagar o erro para ser tratado no useEffect
      } else {
        enqueueSnackbar('Erro ao carregar diários: ' + (error.message || 'Erro desconhecido'), { variant: 'error' });
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [page, searchInput, enqueueSnackbar, getDateRange]);

  // Efeito para lidar com a autenticação e carregamento inicial de diários
  useEffect(() => {
    const loadData = async () => {
      // Se a autenticação ainda está carregando, aguardamos
      if (authLoading) {
        console.log('DiaryList: Aguardando carregamento da autenticação');
        return;
      }
      
      // Se não estamos autenticados e já tentamos carregar, não fazemos nada
      // O redirecionamento será tratado pelo PrivateRoute
      if (!signed && initialLoadAttempted.current) {
        console.log('DiaryList: Usuário não autenticado e já tentamos carregar');
        return;
      }
      
      initialLoadAttempted.current = true;
      
      // Se o usuário estiver autenticado, carregamos os diários
      if (signed && user) {
        console.log('DiaryList: Usuário autenticado, carregando diários inicialmente');
        try {
          await fetchDiaries();
        } catch (error) {
          console.error('Erro ao carregar diários:', error);
          
          // Se for erro de autenticação, tentamos validar o token
          if (error.response && error.response.status === 401) {
            console.log('DiaryList: Erro de autenticação, tentando validar token');
            const isValid = await validateToken();
            
            if (isValid) {
              console.log('DiaryList: Token validado, tentando carregar diários novamente');
              await fetchDiaries();
            }
          }
        }
      }
    };
    
    loadData();
  }, [user, signed, authLoading, navigate, validateToken, fetchDiaries]);

  const handleSearch = () => {
    setPage(1);
    fetchDiaries();
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleDateRangeChange = (event) => {
    const newDateRange = event.target.value;
    setDateRange(newDateRange);
  };

  const handleCustomDateChange = (event) => {
    const newDate = event.target.value;
    setCustomDate(newDate);
  };

  const handleClearSearch = () => {
    setSearchInput('');
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchDiaries(); // Aqui precisamos manter a busca ao mudar de página
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedDiaries(diaries.map(diary => diary.id));
    } else {
      setSelectedDiaries([]);
    }
  };

  const handleSelectDiary = (id) => {
    setSelectedDiaries(prev => {
      if (prev.includes(id)) {
        return prev.filter(diaryId => diaryId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleArchiveSelected = async () => {
    try {
      await api.post('/api/diary/bulk/archive', {
        ids: selectedDiaries
      });
      enqueueSnackbar(`${selectedDiaries.length} diários arquivados com sucesso`, { variant: 'success' });
      setSelectedDiaries([]);
      fetchDiaries();
    } catch (error) {
      console.error('Error archiving diaries:', error);
      enqueueSnackbar('Erro ao arquivar diários', { variant: 'error' });
    }
    setConfirmArchiveDialog(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Draft':
        return '📋';
      case 'Published':
        return '✅';
      case 'Archived':
        return '🟣';
      default:
        return '';
    }
  };

  const canEditDiary = (diary) => {
    return user?.profile === 'Administrador' || diary.status === 'Draft';
  };

  const renderUserWithAvatar = (user) => {
    if (!user) return '-';
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        '& .MuiAvatar-root': {
          width: 24,
          height: 24,
          fontSize: '0.875rem'
        }
      }}>
        <UserAvatar user={user} />
        <Typography variant="body2" noWrap>{user.name}</Typography>
      </Box>
    );
  };

  // Verificar se o usuário tem lares vinculados
  const hasLinkedHomes = user?.profile === 'Administrador' || (user?.laresVinculados && user.laresVinculados.length > 0);

  // Mensagem informativa com base no perfil do usuário
  const getNoHomesMessage = () => {
    if (user?.profile === 'Gestor') {
      return 'Você não possui lares vinculados. Como gestor, você precisa estar vinculado a pelo menos um lar para visualizar e gerenciar diários. Entre em contato com um administrador para vincular você a um ou mais lares.';
    } else if (user?.profile !== 'Administrador') {
      return 'Você não possui lares vinculados. Entre em contato com um administrador para vincular você a um ou mais lares.';
    }
    return '';
  };

  // Exibir componente de carregamento enquanto a autenticação está sendo verificada
  if (authLoading) {
    return <Loading message="Verificando autenticação..." />;
  }

  // Se o usuário não está autenticado, não renderizamos nada
  // O redirecionamento será tratado pelo PrivateRoute
  if (!signed) {
    return <Loading message="Redirecionando..." />;
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Diários</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate('/diary/new')}
          disabled={!hasLinkedHomes && user?.profile !== 'Administrador'}
        >
          Novo Diário
        </Button>
      </Box>

      {!hasLinkedHomes && user?.profile !== 'Administrador' && (
        <Alert 
          severity="info" 
          icon={<InfoIcon />}
          sx={{ mb: 3 }}
        >
          {getNoHomesMessage()}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box display="grid" gridTemplateColumns="1fr 1fr auto" gap={2} alignItems="center">
          <TextField
            label="Pesquisar"
            value={searchInput}
            onChange={handleSearchChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSearch();
              }
            }}
            fullWidth
            variant="outlined"
            placeholder="Digite o nome do residente, profissional ou ID"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box display="flex" gap={2} alignItems="center">
            <FormControl fullWidth>
              <InputLabel>Período</InputLabel>
              <Select
                value={dateRange}
                onChange={handleDateRangeChange}
                label="Período"
              >
                {dateRangeOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {dateRange === 'custom' && (
              <TextField
                label="Data"
                type="date"
                value={customDate}
                onChange={handleCustomDateChange}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
          </Box>
          <Button
            variant="contained"
            onClick={handleSearch}
            sx={{ height: '56px', minWidth: '120px' }}
          >
            Buscar
          </Button>
        </Box>
      </Paper>

      {selectedDiaries.length > 0 && (
        <Box mb={2}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setConfirmArchiveDialog(true)}
          >
            Arquivar Selecionados ({selectedDiaries.length})
          </Button>
        </Box>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : diaries.length === 0 ? (
        <Box textAlign="center" my={4}>
          <Typography variant="body1" color="textSecondary">
            {hasLinkedHomes ? 'Nenhum diário encontrado para os filtros selecionados' : 'Você não tem acesso a nenhum diário'}
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedDiaries.length === diaries.length}
                    indeterminate={selectedDiaries.length > 0 && selectedDiaries.length < diaries.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Lar</TableCell>
                <TableCell>Profissional</TableCell>
                <TableCell>Residente</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Criado em</TableCell>
                <TableCell>Atualizado em</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {diaries.map((diary) => (
                <TableRow key={diary.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedDiaries.includes(diary.id)}
                      onChange={() => handleSelectDiary(diary.id)}
                    />
                  </TableCell>
                  <TableCell>{diary.id}</TableCell>
                  <TableCell>{diary.lar?.nome}</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>{renderUserWithAvatar(diary.professional)}</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>{renderUserWithAvatar(diary.resident)}</TableCell>
                  <TableCell>
                    <Tooltip title={diary.status}>
                      <span>{getStatusIcon(diary.status)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {diary.date_created && format(new Date(diary.date_created), 'dd/MM/yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    {diary.date_updated && format(new Date(diary.date_updated), 'dd/MM/yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Visualizar">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/diary/${diary.id}`)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {canEditDiary(diary) && (
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/diary/${diary.id}/edit`)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box mt={2}>
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </Box>

      <Dialog
        open={confirmArchiveDialog}
        onClose={() => setConfirmArchiveDialog(false)}
      >
        <DialogTitle>Confirmar Arquivamento</DialogTitle>
        <DialogContent>
          Tem certeza que deseja arquivar {selectedDiaries.length} diários?
          Diários arquivados não podem ser modificados.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmArchiveDialog(false)}>Cancelar</Button>
          <Button onClick={handleArchiveSelected} color="secondary">
            Arquivar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DiaryList;
